import { Box, Button, Grid, Menu, MenuItem, Skeleton, styled, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ElementItemProps } from "../types";
import ElementFrame from "./ElementFrame";
import { useDevice } from "../../../../utils/DeviceProvider";
import { useEditedColor } from "../../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";
import { getLabel } from "../../../../utils/utils";
import { useLocale } from "../../../../locales/LocaleProvider";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import UndoIcon from "@mui/icons-material/Undo";
import ClearIcon from "@mui/icons-material/Clear";
import { isEqual } from "lodash-es";

const StyledTable = styled("table")(({ theme }) => ({
  borderSpacing: 0,
  borderColor: theme.palette.divider,
  borderWidth: 0.5,
  borderStyle: "solid",
}));
const StyledTd = styled("td")(({ theme }) => ({
  borderColor: theme.palette.divider,
  borderWidth: 0.5,
  borderStyle: "solid",
  minWidth: 75,
  height: 26,
  display: "table-cell",
  fontSize: 14,
}));
const StyledInput = styled("input")(() => ({
  width: "100%",
  fontSize: 14,
}));

type TableCellProperties = {
  row: number;
  column: number;
  defaultValue: string;
  savedValue: string | null;
  onChange: (p: { value: string; row: number; column: number }) => void;
  onPaste: (p: { values: string[][]; row: number; column: number }) => void;
  selectDirection: (direction: "up" | "down" | "left" | "right" | "tab", row: number, column: number) => void;
};
const TableCell = ({ row, column, defaultValue, savedValue, onChange, onPaste, selectDirection }: TableCellProperties) => {
  const [innerValue, setInnerValue] = useState("");
  const [selected, setSelected] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const { background: editedBackground, text: editedText } = useEditedColor();
  const { formatMessage } = useIntl();

  const onChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setInnerValue(e.target.value.replace(/,/g, "")), []);

  const onKeyDownHandler = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!inputRef.current || e.shiftKey || e.altKey) {
        return;
      }
      switch (e.code) {
        case "Escape":
          inputRef.current.blur();
          break;
        case "ArrowUp":
          selectDirection("up", row, column);
          break;
        case "ArrowDown":
        case "NumpadEnter":
        case "Enter":
          selectDirection("down", row, column);
          break;
        case "ArrowLeft":
          selectDirection("left", row, column);
          break;
        case "ArrowRight":
          selectDirection("right", row, column);
          break;
        case "Tab":
          selectDirection("tab", row, column);
          break;
        default:
          return;
      }
      e.preventDefault();
      e.stopPropagation();
    },
    [selectDirection, row, column]
  );

  const onPasteHandler = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const paste = e.clipboardData.getData("text");
      if (paste) {
        const pasteValues = paste
          .replace(/\r\n/g, "\n")
          .split("\n")
          .map((line) => line.split("\t").map((v) => v.replace(/,/g, "")));
        onPaste({ values: pasteValues, row, column });
      }
    },
    [column, onPaste, row]
  );

  const contextMenuHandler = useCallback((event: React.MouseEvent<HTMLTableCellElement>) => {
    setAnchorEl(event.currentTarget);
    setContextMenuOpen(true);
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const select = useCallback(() => setSelected(true), []);

  const deselect = useCallback(() => {
    setSelected(false);
    onChange({ value: innerValue, row, column });
  }, [innerValue, onChange, row, column]);

  const resetValue = useCallback(() => {
    setContextMenuOpen(false);
    setTimeout(() => {
      setInnerValue(savedValue ?? "");
      onChange({ value: savedValue ?? "", row, column });
    }, 0);
    deselect();
  }, [column, deselect, onChange, row, savedValue]);

  const closeContextMenu = useCallback(() => setContextMenuOpen(false), []);

  useEffect(() => {
    setInnerValue(defaultValue);
  }, [defaultValue]);

  const input = useMemo(
    () => (
      <StyledInput
        ref={inputRef}
        autoFocus
        type="text"
        value={innerValue}
        onChange={onChangeHandler}
        autoComplete="false"
        onBlur={deselect}
        onKeyDown={onKeyDownHandler}
        onPaste={onPasteHandler}
      />
    ),
    [deselect, innerValue, onChangeHandler, onKeyDownHandler, onPasteHandler]
  );

  return (
    <>
      <StyledTd
        className="restoreContextMenuTarget"
        sx={defaultValue === null || (savedValue ?? "") === innerValue ? {} : { backgroundColor: editedBackground, color: editedText }}
        onClick={select}
        onContextMenu={!selected && !(savedValue === null && innerValue === "") && savedValue !== innerValue ? contextMenuHandler : undefined}
      >
        {selected ? input : innerValue}
      </StyledTd>
      <Menu
        anchorEl={anchorEl}
        open={contextMenuOpen}
        onClose={closeContextMenu}
        MenuListProps={{
          "aria-labelledby": "lock-button",
          role: "listbox",
        }}
      >
        <MenuItem selected={false} onClick={resetValue}>
          {formatMessage({ defaultMessage: "Reset to saved value" })}
        </MenuItem>
      </Menu>
    </>
  );
};

export interface TableParameters {
  rows: number;
  columns: number;
}

const Table = ({ label: labelParam, rows, columns, configKey, elementKey, disabled }: ElementItemProps<TableParameters>) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<string[][] | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const tableRef = useRef<HTMLTableElement>(null);

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { formatMessage } = useIntl();
  const { locale } = useLocale();

  const label = getLabel(labelParam, locale);

  const emptyValue = useMemo(() => Array.from({ length: rows }).map(() => Array.from({ length: columns }).map(() => "")), [rows, columns]);

  const simplifyValue = useCallback((value: string[][] | null) => {
    if (value === null || !value.some((line) => line.some((val) => val !== ""))) {
      value = null;
    }
    return value;
  }, []);

  const value = useMemo(() => {
    const newValue = currentValue && configIndex !== null ? (currentValue[configKey].items[configIndex].value as string[][]) : null;
    newValue?.forEach((line) =>
      line.forEach((v, j) => {
        if (v === undefined) {
          line[j] = "";
        }
      })
    );
    return simplifyValue(newValue);
  }, [currentValue, configIndex, configKey, simplifyValue]);

  const editCurrentValue = useCallback(
    (editedValue: string[][] | null) => {
      if (!currentValue || configIndex === null) {
        return;
      }

      currentValue[configKey].items[configIndex].value = simplifyValue(editedValue);
      setCurrentValue(currentValue);
    },
    [currentValue, configIndex, configKey, simplifyValue, setCurrentValue]
  );

  const cellChangeHandler = useCallback(
    ({ value: v, row, column }: { value: string; row: number; column: number }) => {
      if (configIndex === null) {
        return;
      }
      if ((value && value[row][column] === v) || row < 0 || row >= rows || column < 0 || column >= columns) {
        return;
      }
      const editedValue = value ?? emptyValue;
      editedValue[row][column] = v;
      editCurrentValue(editedValue);
    },
    [columns, configIndex, editCurrentValue, emptyValue, rows, value]
  );

  const cellPasteHandler = useCallback(
    ({ values, row, column }: { values: string[][]; row: number; column: number }) => {
      const editedValue = value ?? emptyValue;
      for (let i = 0; i < values.length && row + i < rows; i++) {
        for (let j = 0; j < values[i].length && column + j < columns; j++) {
          editedValue[row + i][column + j] = values[i][j];
        }
      }
      editCurrentValue(editedValue);
    },
    [value, emptyValue, editCurrentValue, rows, columns]
  );

  const selectHandler = useCallback(
    (direction: "up" | "down" | "left" | "right" | "tab", row: number, column: number) => {
      if (
        (row === 0 && direction === "up") ||
        (row === rows - 1 && direction === "down") ||
        (column === 0 && direction === "left") ||
        (column === columns - 1 && direction === "right") ||
        (column === columns - 1 && row === rows - 1 && direction === "tab")
      ) {
        return;
      }
      const selectRow = direction === "tab" ? (column === columns - 1 ? row + 1 : row) : direction === "up" ? row - 1 : direction === "down" ? row + 1 : row;
      const selectColumn =
        direction === "tab" ? (column === columns - 1 ? 0 : column + 1) : direction === "left" ? column - 1 : direction === "right" ? column + 1 : column;
      const currentCell = tableRef.current?.children[0].children[row]?.children[column]?.children[0];
      const selectableCell = tableRef.current?.children[0].children[selectRow]?.children[selectColumn];

      if (!currentCell || !selectableCell) {
        return;
      }
      (currentCell as HTMLInputElement).blur();
      (selectableCell as HTMLTableCellElement).click();
    },
    [columns, rows]
  );

  const copyToClipboard = useCallback(() => {
    if (!value) {
      return;
    }
    const copyValue = value.map((line) => line.join("\t")).join("\n");
    navigator.clipboard.writeText(copyValue);
  }, [value]);

  const importCsv = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = function (e) {
          const view = e.target === null || typeof e.target === "string" ? null : new Uint8Array(e.target.result as ArrayBuffer);
          const data = view ? new TextDecoder().decode(view).replace(/;/g, ",").replace(/\r/g, "") : e.target;
          if (data && typeof data === "string") {
            const importedValue = data.split("\n").map((line) => line.split(",").map((v) => v.replace(/,/g, "")));
            const editedValue = [] as string[][];
            for (let i = 0; i < rows; i++) {
              editedValue.push([] as string[]);
              for (let j = 0; j < columns; j++) {
                editedValue[i].push(importedValue[i]?.[j] ?? "");
              }
            }
            editCurrentValue(editedValue);
          }
        };
        reader.readAsArrayBuffer(file);
      }
    },
    [columns, editCurrentValue, rows]
  );

  const resetValue = useCallback(() => {
    editCurrentValue(saved);
  }, [editCurrentValue, saved]);

  const clearTable = useCallback(() => {
    editCurrentValue(emptyValue);
  }, [editCurrentValue, emptyValue]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key?: string }) => type === "TABLE" && (key === elementKey || `${key}` === elementKey)) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }

    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? null;
    setSaved(svd);
    setDataLoaded(true);
  }, [config, configIndex, configKey, currentValue]);

  if (configIndex === null || !dataLoaded) {
    return (
      <ElementFrame label={label}>
        <Skeleton variant="rectangular" height={40} sx={{ width: "100%", borderRadius: 1 }} />
      </ElementFrame>
    );
  }

  if (configIndex === -1) {
    return (
      <ElementFrame label={label}>
        <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>
      </ElementFrame>
    );
  }

  return (
    <ElementFrame label={label} disabled={disabled}>
      <Box
        sx={{
          display: "flex",
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        <StyledTable ref={tableRef}>
          <tbody>
            {emptyValue.map((line, i) => (
              <tr key={`table-line-${i}`}>
                {line.map((emptyCellValue, j) => (
                  <TableCell
                    key={`table-cell-${j}`}
                    row={i}
                    column={j}
                    defaultValue={value?.[i]?.[j] ?? emptyCellValue}
                    savedValue={saved ? saved[i]?.[j] : null}
                    onChange={cellChangeHandler}
                    onPaste={cellPasteHandler}
                    selectDirection={selectHandler}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </Box>

      {!disabled ? (
        <Grid container spacing={1}>
          <Grid item>
            <Button startIcon={<ContentCopyIcon />} onClick={copyToClipboard} disabled={!value}>
              {formatMessage({ defaultMessage: "Copy to clipboard" })}
            </Button>
          </Grid>
          <Grid item>
            <Button component="label" startIcon={<FileOpenIcon />}>
              {formatMessage({ defaultMessage: "Import CSV" })}
              <input type="file" hidden value="" onChange={importCsv} />
            </Button>
          </Grid>
          <Grid item>
            <Button startIcon={<UndoIcon />} color="warning" onClick={resetValue} disabled={isEqual(saved, value)}>
              {formatMessage({ defaultMessage: "Reset to saved value" })}
            </Button>
          </Grid>
          <Grid item>
            <Button startIcon={<ClearIcon />} color="error" onClick={clearTable} disabled={!value}>
              {formatMessage({ defaultMessage: "Clear table" })}
            </Button>
          </Grid>
        </Grid>
      ) : null}
    </ElementFrame>
  );
};

export default Table;
